import { Location } from 'history';
import replace from 'lodash/replace';

export const LoginPath = '/login';
export const ImpersonatePath = '/impersonate/:marketplaceId/:token';
export const LogoutPath = '/logout';
export const ForgotPasswordPath = '/forgotpassword';
export const ForgotPasswordSentPath = '/forgotpassword_sent';
export const ResetPasswordPath = '/resetpassword/:token';
export const NewPasswordPath = '/newpassword';
export const ResetCustomerPath = '/resetpassword_customer/:token';
export const SignUpPath = '/signup';
export const SignUpAlmostPath = '/signup/almost';
export const SignUpBusinessPath = '/signup/business';
export const SignUpVerifyPath = '/signup/verify';
export const ActivateAccountPath = '/activate_business/:token';
export const AgreementsSignaturePath = '/agreements/signature/:hash';
export const EmailUnsubscribePath = '/unsubscribe/:token';
export const TwoWaySMSPath = '/sms';
export const InboxPath = '/inbox';

export const HomePath = '/';
export const CalendarPath = '/calendar';

export const BookingsRootPath = '/bookings';
export const BookingsPath = '/bookings/*';
export const BookingsGroomingPath = '/bookings/grooming';
export const BookingsGroomingDetailPath = '/bookings/grooming/:hash';
export const BookingsKanbanPath = '/bookings/kanban';
export const BookingsAbandonedPath = '/bookings/abandoned';
export const BookingsDaycarePath = '/bookings/daycare';
export const BookingsDaycareDetailPath = '/bookings/daycare/:hash';
export const BookingsBoardingPath = '/bookings/boarding';
export const BookingsBoardingDetailPath = '/bookings/boarding/:hash';
export const BookingsEvaluationPath = '/bookings/evaluation';
export const BookingsEvaluationDetailPath = '/bookings/evaluation/:hash';
export const BookingsRequestedPath = '/bookings/requested';
export const BookingsRequestedDetailPath = '/bookings/requested/:hash';
export const BookingsAllPath = '/bookings/all';
export const BookingsAllDetailPath = '/bookings/all/:hash';
export const BookingsTransportationPath = '/bookings/transportation';
export const BookingsTransportationDetailPath = '/bookings/transportation/:hash';
export const BookingsTrainingPath = '/bookings/training';
export const BookingsTrainingDetailPath = '/bookings/training/:hash';
export const BookingsWalkingPath = '/bookings/walking';
export const BookingsWalkingDetailPath = '/bookings/walking/:hash';

export const ServicesPath = '/services';
export const MembershipsPath = '/memberships';
export const DaycarePath = '/daycare';
export const BoardingPath = '/boarding';
export const DiscountsPath = '/discounts';
export const TransportationPath = '/transportation';
export const TrainingPath = '/training';
export const WalkingPath = '/walking';
export const CreateServiceAddonPath = '/services/addons/create';
export const GlobalPath = '/globalResult/:query';
export const CreationServicePath = '/services/:create';
export const ClientsPath = '/clients';
export const AgreementsPath = '/clients#agreements';
export const SoldMembershipsPath = '/clients#sold-memberships';
export const FormsPath = '/clients#forms';
export const VaccineApprovalsPath = '/clients#vaccine-approvals';
export const ClientsEditPath = '/clients/:hash';
export const StaffPath = '/staff';
export const BusinessSettingsPath = '/business_settings';
export const MyAccountPath = '/my_account';
export const InsightsPath = '/insights';
export const ProductsPath = '/products';
export const PaymentsPath = '/payments';
export const HelpCenterPath = 'https://help.cuddlesapp.com/kb/en';
export const HelpSupportPath = '/help_support';
export const TasksPath = '/tasks';
export const TasksFeedingPath = '/tasks/feeding';
export const TasksMedsPath = '/tasks/meds';
export const TasksOpsPath = '/tasks/ops';
export const TasksBelongingsPath = '/tasks/belongings';
export const NotAllowedPath = '/not-allowed';
export const AiAgentPath = '/ai-agent';
/**
 * Marketing route paths
 */
export const MarketingPath = '/marketing';
export const MarketingReviewsPath = '/marketing/reviews';
export const MarketingLeadsPath = '/marketing/leads';
export const MarketingLoyaltyPath = '/marketing/loyalty';
export const SMSGetStartedPath = '/marketing/sms/get-started';
export const SMSCampaignCreatePath = '/marketing/sms/create';
export const MarketingPromoCodes = '/marketing/promo-codes';
export const MarketingAutomationsPath = '/marketing/automations';
export const MarketingEmailsPath = '/marketing/emails';
export const EmailTemplateCreatePath = '/marketing/emails/templates/new';
export const EmailTemplateEditPath = '/marketing/emails/templates/:id/edit';
export const EmailCampaignDetailPath = '/marketing/emails/campaigns/:id';
/**
 * Settings route paths
 */

export const BusinessPlanPath = '/business_settings/info/plans';

/**
 * Data Export route path
 */
export const DataExportPath = '/business_settings/settings/data-export';

/**
 * Pay By Link
 */
export const PayByLinkPath = '/pay-by-link/:uuid';

export const UpdateVaccinesPath = '/update-vaccines/:uuid';

/**
 * Leave reviews
 */
export const LeaveReviewPath = '/leave-review/:uuid';

export const isPublic = (location: Location): boolean => {
    const path = location.pathname;

    const publicPaths = [
        LoginPath,
        ImpersonatePath,
        LogoutPath,
        ForgotPasswordPath,
        ForgotPasswordSentPath,
        ResetPasswordPath,
        NewPasswordPath,
        ResetCustomerPath,
        SignUpPath,
        SignUpAlmostPath,
        SignUpBusinessPath,
        SignUpVerifyPath,
        ActivateAccountPath,
        AgreementsSignaturePath,
        EmailUnsubscribePath,
        PayByLinkPath,
        UpdateVaccinesPath,
        LeaveReviewPath
    ].map(path => replace(path, /\/:.*/g, ''));

    return publicPaths.some(p => path.startsWith(p));
};

export const isPrivate = (location: Location): boolean => {
    return !isPublic(location);
};

export const requiresCleanUp = (location: Location): boolean => {
    const path = location.pathname;

    const avoidCleanUpPublicPaths = [
        ForgotPasswordSentPath,
        NewPasswordPath,
        SignUpAlmostPath,
        SignUpBusinessPath,
        SignUpVerifyPath
    ].map(path => replace(path, /\/:.*/g, ''));

    return isPublic(location) && !avoidCleanUpPublicPaths.some(p => path.startsWith(p));
};

export type Path =
    | typeof LoginPath
    | typeof ForgotPasswordPath
    | typeof ForgotPasswordSentPath
    | typeof ResetPasswordPath
    | typeof ResetCustomerPath
    | typeof SignUpPath
    | typeof SignUpAlmostPath
    | typeof SignUpBusinessPath
    | typeof SignUpVerifyPath
    | typeof ActivateAccountPath
    | typeof HomePath
    | typeof CalendarPath
    | typeof GlobalPath
    | typeof BookingsPath
    | typeof ServicesPath
    | typeof MembershipsPath
    | typeof DaycarePath
    | typeof BoardingPath
    | typeof TransportationPath
    | typeof TrainingPath
    | typeof WalkingPath
    | typeof CreationServicePath
    | typeof ClientsPath
    | typeof StaffPath
    | typeof BusinessSettingsPath
    | typeof MyAccountPath
    | typeof InsightsPath
    | typeof ProductsPath
    | typeof PaymentsPath
    | typeof AgreementsSignaturePath
    | typeof HelpCenterPath
    | typeof HelpSupportPath
    | typeof PayByLinkPath
    | typeof UpdateVaccinesPath
    | typeof LeaveReviewPath
    | typeof NotAllowedPath
    | typeof AiAgentPath;
